/* prettier-ignore-file */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
} from "react-native";
import { functions, db } from "../firebase.config";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { trustDevice, isDeviceTrusted } from "../utils/deviceTrust";
import { useNavigate } from "react-router-dom";

const PinAuthLayer = ({ onAuthenticated, bookingId }) => {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [pinLocked, setPinLocked] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const pinInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if device is already trusted
    if (isDeviceTrusted(bookingId)) {
      console.log("✅ Device already trusted, bypassing PIN");
      onAuthenticated();
      return;
    }

    // Check if PIN is locked
    checkPinStatus();

    // Auto-focus PIN input
    if (pinInputRef.current) {
      setTimeout(() => {
        pinInputRef.current?.focus();
      }, 100);
    }
  }, [bookingId]);

  const checkPinStatus = async () => {
    // Skip checking PIN status for unauthenticated users - they'll find out when they try to verify
    // The Cloud Function will handle PIN verification and return the status
    return;
  };

  const handlePinChange = (text) => {
    // Only allow 3 digits
    const cleaned = text.replace(/\D/g, "").slice(0, 3);
    setPin(cleaned);
    setError("");
    
    // Auto-submit when 3 digits entered
    if (cleaned.length === 3) {
      handleVerifyPin(cleaned);
    }
  };

  const handleVerifyPin = async (pinToVerify = null) => {
    const pinValue = pinToVerify !== null ? pinToVerify : pin;
    
    if (pinValue.length !== 3) {
      setError("Please enter a 3-digit PIN");
      return;
    }

    if (pinLocked) {
      setError("PIN is locked. Please request a PIN reset.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("🔐 Verifying PIN for booking:", bookingId);

      const verifyPin = httpsCallable(functions, "verifyBookingPin");
      const result = await verifyPin({
        bookingId: bookingId,
        pin: pinValue,
      });

      if (result.data.success) {
        console.log("✅ PIN verified successfully");
        
        // Trust device if checkbox is checked
        if (rememberDevice) {
          trustDevice(bookingId);
        }

        // Call the authenticated callback
        onAuthenticated();
      } else {
        setError(result.data.error || "Invalid PIN. Please try again.");
        setPin(""); // Clear PIN for security
        setFailedAttempts(result.data.failedAttempts || 0);
        
        // Check if PIN is now locked
        if (result.data.pinLocked) {
          setPinLocked(true);
          setError("PIN is locked due to too many failed attempts. Please request a PIN reset.");
        }
      }
    } catch (error) {
      console.error("❌ Error verifying PIN:", error);
      setError(error.message || "Failed to verify PIN. Please try again.");
      setPin(""); // Clear PIN on error
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPinReset = async () => {
    try {
      setLoading(true);
      setError("");

      const requestPinReset = httpsCallable(functions, "requestPinReset");
      const result = await requestPinReset({
        bookingId: bookingId,
      });

      if (result.data.success) {
        setError("");
        alert("✅ PIN reset link has been sent to your email. Please check your inbox.");
      } else {
        setError(result.data.error || "Failed to request PIN reset. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error requesting PIN reset:", error);
      setError(error.message || "Failed to request PIN reset. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      visible={true} 
      transparent 
      animationType="fade"
      onRequestClose={() => {}} // Prevent closing during loading
    >
      <View style={styles.overlay} pointerEvents="auto">
        <View style={[styles.container, loading && styles.containerLoading]} pointerEvents={loading ? "none" : "auto"}>
          <Text style={styles.title}>🔐 Enter Booking PIN</Text>
          <Text style={styles.subtitle}>
            Please enter the 3-digit PIN sent to your email to access this booking.
          </Text>

          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {pinLocked && (
            <View style={styles.lockedContainer}>
              <Text style={styles.lockedText}>
                ⚠️ PIN is locked due to too many failed attempts.
              </Text>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.resetButton}
                onPress={handleRequestPinReset}
                disabled={loading}
              >
                <Text style={styles.resetButtonText}>Request PIN Reset</Text>
              </TouchableOpacity>
            </View>
          )}

          {!pinLocked && (
            <>
              <View style={styles.pinContainer}>
                <TextInput
                  ref={pinInputRef}
                  style={[styles.pinInput, loading && styles.pinInputDisabled]}
                  value={pin}
                  onChangeText={handlePinChange}
                  keyboardType="number-pad"
                  maxLength={3}
                  secureTextEntry={false}
                  autoFocus={!loading}
                  placeholder="000"
                  placeholderTextColor="#8A9794"
                  editable={!loading}
                />
              </View>

              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={styles.checkbox}
                  onPress={() => setRememberDevice(!rememberDevice)}
                  disabled={loading}
                  activeOpacity={loading ? 1 : 0.7}
                >
                  <View style={[styles.checkboxBox, rememberDevice && styles.checkboxChecked, loading && styles.checkboxDisabled]}>
                    {rememberDevice && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={[styles.checkboxLabel, loading && styles.checkboxLabelDisabled]}>
                    Remember this device for 60 days
                  </Text>
                </TouchableOpacity>
              </View>

              {loading && (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="small" color="#0E7C6B" />
                </View>
              )}

              {failedAttempts > 0 && failedAttempts < 3 && (
                <Text style={styles.attemptsText}>
                  {3 - failedAttempts} attempt{3 - failedAttempts !== 1 ? "s" : ""} remaining
                </Text>
              )}
            </>
          )}

          <TouchableOpacity
            style={styles.resetLink}
            onPress={handleRequestPinReset}
            disabled={loading || pinLocked}
          >
            <Text style={styles.resetLinkText}>Forgot PIN? Request Reset</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 30,
    width: "100%",
    maxWidth: 400,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 10,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 30,
    lineHeight: 20,
  },
  pinContainer: {
    width: "100%",
    marginBottom: 20,
  },
  pinInput: {
    fontSize: 32,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 20,
    padding: 15,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 8,
    backgroundColor: "#F4F6F6",
    color: "#2E3A38",
  },
  checkboxContainer: {
    width: "100%",
    marginBottom: 20,
  },
  checkbox: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxBox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 4,
    marginRight: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkmark: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#2E3A38",
  },
  errorContainer: {
    backgroundColor: "#FBEAE9",
    borderLeftWidth: 4,
    borderLeftColor: "#C4453D",
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    width: "100%",
  },
  errorText: {
    color: "#C4453D",
    fontSize: 14,
    lineHeight: 20,
  },
  lockedContainer: {
    width: "100%",
    marginBottom: 20,
    alignItems: "center",
  },
  lockedText: {
    fontSize: 14,
    color: "#C4453D",
    textAlign: "center",
    marginBottom: 15,
    lineHeight: 20,
  },
  resetButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
  },
  resetButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  loadingContainer: {
    marginTop: 10,
    marginBottom: 10,
  },
  attemptsText: {
    fontSize: 12,
    color: "#C4453D",
    marginTop: 10,
  },
  resetLink: {
    marginTop: 20,
    padding: 10,
  },
  resetLinkText: {
    color: "#0E7C6B",
    fontSize: 14,
    textDecorationLine: "underline",
  },
  pinInputDisabled: {
    opacity: 0.6,
    backgroundColor: "#EDF1F0",
  },
  checkboxDisabled: {
    opacity: 0.6,
  },
  checkboxLabelDisabled: {
    opacity: 0.6,
  },
  containerLoading: {
    opacity: 0.8,
  },
});

export default PinAuthLayer;

